import { useRef } from 'react';
import { Paperclip } from 'lucide-react';
import type { Editor } from '@tiptap/react';
import type { Attachment } from '@rundown/types';
import { cx } from '../../lib/cx';

/**
 * The two things you can do with a file here, and which one happens when.
 *
 * ── Pasting ─────────────────────────────────────────────────────────────────
 * An image goes INLINE, because a screenshot pasted into a bug report is part
 * of what is being said — "it looks like this" — and putting it in a strip
 * underneath separates the evidence from the sentence it belongs to. Anything
 * else (a PDF, a CSV, a har file) becomes an attachment, because there is
 * nothing useful to render inline and a filename in a strip is the honest
 * representation of it.
 *
 * ── The paperclip ───────────────────────────────────────────────────────────
 * Always attaches, images included. That is the whole point of it: someone who
 * wants a screenshot on the record without three hundred pixels of it in the
 * middle of their paragraph has no way to express that through pasting alone.
 */

/** Images inline, everything else attached. */
export function splitByDestination(files: readonly File[]): {
  inline: File[];
  attach: File[];
} {
  const inline: File[] = [];
  const attach: File[] = [];
  for (const file of files) {
    // Not `image/svg+xml`: an SVG is a document that can carry script, and
    // rendering one inline from a client's clipboard is a cross-site scripting
    // vector wearing a picture's clothes. It attaches instead.
    if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') inline.push(file);
    else attach.push(file);
  }
  return { inline, attach };
}

/** Every file on a drop. Empty for a drag within the document itself. */
export function filesFromDrop(event: DragEvent): File[] {
  return [...(event.dataTransfer?.files ?? [])];
}

/** Every file on a clipboard event, including a screenshot with no filename. */
export function filesFromClipboard(event: ClipboardEvent): File[] {
  const files: File[] = [];
  for (const item of event.clipboardData?.items ?? []) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file !== null) files.push(file);
  }
  return files;
}

/**
 * Put uploaded images into the document at the cursor.
 *
 * Inserted after the upload lands rather than optimistically, so the node
 * always carries a real attachment id and the document is never briefly
 * unsaveable. For a pasted screenshot that is a few hundred milliseconds; the
 * pending strip shows progress in the meantime, so nothing looks stuck.
 */
export function insertImages(editor: Editor, attachments: readonly Attachment[]): void {
  if (attachments.length === 0) return;
  editor
    .chain()
    .focus()
    .insertContent(
      attachments.map((attachment) => ({
        type: 'image',
        attrs: { attachmentId: attachment.id, src: attachment.url, alt: attachment.filename },
      })),
    )
    .run();
}

/**
 * The paperclip: choose files, always as attachments.
 *
 * A real `<input type="file">` kept off-screen rather than a fabricated click
 * on a hidden element — the file picker can only be opened by a genuine user
 * gesture on an input, and every other approach is a workaround that browsers
 * periodically tighten.
 */
export function AttachButton({
  onFiles,
  disabled = false,
  className,
  label = 'Attach a file',
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        type="button"
        title={label}
        aria-label={label}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={cx('rounded-md p-1.5 text-muted hover:bg-surface-3 hover:text-ink', className)}
      >
        <Paperclip size={14} />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          // Cleared so choosing the same file twice in a row still fires a
          // change event the second time.
          event.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
    </>
  );
}
